import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { createManagedUser, getEmbeddedUsers, getPricingOptions } from '../../../lib/api';
import './admin.css';

const CreateUser = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [pricingTier, setPricingTier] = useState('');
  const [embeddedSelectId, setEmbeddedSelectId] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [pricingOptions, setPricingOptions] = useState([]);
  const [embeddedUsers, setEmbeddedUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [pricing, embedded] = await Promise.all([
          getPricingOptions(),
          getEmbeddedUsers(),
        ]);
        setPricingOptions(pricing?.data || []);
        setEmbeddedUsers(embedded?.data || []);
      } catch (e) {
        setError(e?.message || 'Failed to load required data');
      }
    };
    loadData();
  }, []);

  const onPhoneSelect = (id) => {
    setEmbeddedSelectId(id);
    const selected = embeddedUsers.find((u) => String(u.id) === String(id));
    if (selected) {
      setWabaId(selected.waba_id || '');
      setPhoneNumberId(selected.number_id || '');
    } else {
      setWabaId('');
      setPhoneNumberId('');
    }
  };

  const onSubmit = async () => {
    setMessage('');
    setError('');
    if (!email || !password || !name || !pricingTier || !wabaId || !phoneNumberId) {
      setError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    try {
      await createManagedUser({
        email,
        password,
        name,
        wa_pricing: Number(pricingTier),
        balance: 0,
        waba_id: wabaId,
        phone_number_id: phoneNumberId,
        status: 1,
        role: 'user',
      });
      setMessage('User created successfully.');
      setEmail('');
      setPassword('');
      setName('');
      setPricingTier('');
      setEmbeddedSelectId('');
      setWabaId('');
      setPhoneNumberId('');
    } catch (e) {
      setError(e?.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page__hero">
        <div className="admin-page__title-row">
          <div className="admin-page__icon" aria-hidden>
            <FontAwesomeIcon icon={faUserPlus} />
          </div>
          <div>
            <span className="admin-page__eyebrow">Administration</span>
            <h1 className="admin-page__title">Create user</h1>
            <p className="admin-page__subtitle">
              Add a new account with pricing, then link it to an embedded phone number so WABA and phone IDs are filled automatically.
            </p>
          </div>
        </div>
      </header>

      <section className="admin-panel">
        <div className="admin-panel__head">
          <h2>New user details</h2>
          <p>All fields are required before you can create the user.</p>
        </div>
        <div className="admin-panel__body">
          {error && (
            <div className="admin-feedback admin-feedback--error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}
          <div className="admin-grid">
            <div className="admin-field">
              <span className="admin-label">Email</span>
              <input
                className="admin-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                autoComplete="off"
              />
            </div>
            <div className="admin-field">
              <span className="admin-label">Password</span>
              <input
                className="admin-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Temporary password"
                autoComplete="new-password"
              />
            </div>
            <div className="admin-field">
              <span className="admin-label">Display name</span>
              <input
                className="admin-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name or business name"
              />
            </div>
            <div className="admin-field">
              <span className="admin-label">Pricing tier</span>
              <select
                className="admin-select"
                value={pricingTier}
                onChange={(e) => setPricingTier(e.target.value)}
              >
                <option value="">Select pricing tier</option>
                {pricingOptions.map((p) => (
                  <option key={p.pricing_id} value={String(p.pricing_id)}>
                    ID {p.pricing_id} — Marketing ₹{p.marketing}, Utility ₹{p.utility}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-field">
              <span className="admin-label">Embedded phone</span>
              <select
                className="admin-select"
                value={embeddedSelectId}
                onChange={(e) => onPhoneSelect(e.target.value)}
              >
                <option value="">Choose number to auto-fill IDs</option>
                {embeddedUsers.map((e) => (
                  <option key={e.id} value={String(e.id)}>
                    {e.phone_number}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-field">
              <span className="admin-label">WABA ID</span>
              <input className="admin-input" value={wabaId} readOnly placeholder="Select a phone above" />
            </div>
            <div className="admin-field">
              <span className="admin-label">Phone number ID</span>
              <input className="admin-input" value={phoneNumberId} readOnly placeholder="Select a phone above" />
            </div>
          </div>

          <div className="admin-actions">
            <button type="button" className="admin-btn admin-btn--primary" onClick={onSubmit} disabled={submitting}>
              {submitting ? 'Creating…' : 'Create user'}
            </button>
            {message && <span className="admin-feedback admin-feedback--success">{message}</span>}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreateUser;
